import React from 'react'
import { Link } from 'react-router-dom'
import { Check, Star, Shield, Music, Sparkles, Award, Heart, Users, Send } from 'lucide-react'
import SEO from '../components/SEO'

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": "https://www.djron9.dk/om/#webpage",
        "url": "https://www.djron9.dk/om/",
        "name": "Om DJ Ron9 | Professionel DJ med over 25 års erfaring",
        "description": "Mød Ronni bag DJ Ron9. Over 25 års erfaring med musik, lyd og lys til bryllup, firmafest og runde fødselsdage i København og på Sjælland.",
        "breadcrumb": {
          "@id": "https://www.djron9.dk/om/#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://www.djron9.dk/om/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://www.djron9.dk/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Om DJ Ron9",
            "item": "https://www.djron9.dk/om/"
          }
        ]
      },
      {
        "@type": "Person",
        "name": "Ronni Berlau",
        "alternateName": "DJ Ron9",
        "jobTitle": "Professionel DJ & Event Musiker",
        "url": "https://www.djron9.dk/om/",
        "worksFor": {
          "@type": "EntertainmentBusiness",
          "name": "DJ Ron9",
          "url": "https://www.djron9.dk/"
        }
      }
    ]
  }

  const stats = [
    { label: 'Års Erfaring', value: '+25 år' },
  ]

  const values = [
    {
      icon: <Music className="h-6 w-6 text-white" />,
      title: 'Musikalsk Alsidighed',
      description: 'Fra de største 80\'er og 90\'er klassikere til moderne klubhits, hiphop og de nyeste radio-baskere. Jeg tilpasser altid playlisten til jeres gæster.',
    },
    {
      icon: <Users className="h-6 w-6 text-white" />,
      title: 'Evnen til at læse rummet',
      description: 'En god DJ kigger ikke ned i sin pult. Jeg holder konstant øje med dansegulvet og justerer musikken live, så energien forbliver i top hele natten.',
    },
    {
      icon: <Sparkles className="h-6 w-6 text-white" />,
      title: 'Eksklusivt Lyd & Lys',
      description: 'Jeg går ikke på kompromis med kvaliteten. Jeg medbringer professionelt udstyr fra mærker som Yamaha, RCF, Denon og Technics, der sikrer krystalklar lyd.',
    },
    {
      icon: <Heart className="h-6 w-6 text-white" />,
      title: 'Personligt Engagement',
      description: 'For mig er jeres fest ikke bare endnu et job. Vi planlægger aftenen sammen, så alt fra brudevals til natmad forløber præcis, som I drømmer om.',
    },
  ]

  return (
    <div className="bg-black text-white">
      <SEO
        title="Om DJ Ron9 | Professionel DJ med over 25 års erfaring"
        description="Mød Ronni bag DJ Ron9. Over 25 års erfaring med musik, lyd og lys til bryllup, firmafest og runde fødselsdage i København og på Sjælland."
        canonical="/om/"
        ogImage="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/header-billed-om-dj-ron9.jpg"
        schema={aboutSchema}
      />
      {/* Hero Section */}
      <section className="relative flex min-h-[35vh] sm:min-h-[50vh] items-center justify-center overflow-hidden py-12 sm:py-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/header-billed-om-dj-ron9.jpg"
            alt="Om DJ Ron9"
            width="1600"
            height="500"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover opacity-60 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-white">
            Om DJ Ron9
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-zinc-300 sm:text-lg leading-relaxed">
            Professionel DJ-erfaring, passion for den perfekte overgang og en urokkelig garanti for et fyldt dansegulv.
          </p>
          
          {/* Contact CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#bb9b56]/15 transition-all hover:scale-105 hover:bg-[#a88645]"
            >
              <Send className="h-4 w-4" />
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>

      {/* Intro / Bio Section */}
      <section className="py-20 bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Image Column */}
            <div className="lg:col-span-5 relative">
              <div className="relative overflow-hidden border border-zinc-800 bg-zinc-950 rounded-2xl shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/cae1706e-9e2d-4a22-8efb-179f53434740.jpg"
                  alt="Ronni - DJ Ron9"
                  loading="lazy"
                  decoding="async"
                  width="600"
                  height="500"
                  className="object-cover w-full h-[500px] object-top transition-transform duration-500 hover:scale-105"
                />
              </div>
              {/* Decorative gold badge */}
              <div className="absolute -bottom-6 -right-6 hidden sm:flex items-center gap-3 bg-[#bb9b56] p-6 rounded-xl shadow-xl">
                <Award className="h-10 w-10 text-white shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-white/80">Erfaren DJ</p>
                  <p className="text-lg font-extrabold text-white">Siden 1999</p>
                </div>
              </div>
            </div>

            {/* Text Column */}
            <div className="lg:col-span-7 space-y-6 lg:pl-8">
              <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                Musik samler folk og skaber minder
              </h2>
              
              <div className="space-y-4 text-zinc-300 text-sm sm:text-base leading-relaxed">
                <p>
                  Mit navn er Ronni, og bag kunstnernavnet <strong>DJ Ron9</strong> gemmer der sig mere end 25 års intens kærlighed til musikken og festens unikke dynamik. Siden jeg første gang lagde hænderne på en DJ-pult, har min mission været klar: At levere den absolut bedste musikoplevelse og gøre hver eneste fest uforglemmelig.
                </p>
                <p>
                  Jeg har spillet til alt fra intime, romantiske bryllupper på herregårde til massive firmafester med over 500 gæster og eksklusive klubevents. Den brede erfaring har givet mig en helt særlig evne til at læse rummet og lynhurtigt afkode, hvad der skal til for at holde gang i netop jeres dansegulv.
                </p>
                <p>
                  For mig handler DJ-faget ikke om at sætte en fast playliste på og lade den køre. Det handler om nærvær, præcision i overgangene og en dyb forståelse for, hvordan man bygger en aften op – fra den hyggelige loungestemning under middagen til det absolutte klimaks på dansegulvet i de sene nattetimer.
                </p>
              </div>

              {/* Stats */}
              <div className="pt-6 border-t border-zinc-900">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center sm:text-left">
                    <p className="text-2xl sm:text-3xl font-extrabold text-white font-sans">{stat.value}</p>
                    <p className="text-xs text-zinc-500 mt-1 font-medium uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy / Values Section */}
      <section className="py-20 bg-zinc-950/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
              Hvorfor vælge DJ Ron9?
            </h2>
            <p className="mt-4 text-zinc-400 text-sm sm:text-base">
              Når du booker mig, får du ikke bare en mand, der trykker på play. Du får en professionel samarbejdspartner, der sikrer kvalitet hele vejen igennem.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-y-12 mt-16 sm:grid-cols-2 sm:gap-x-12 lg:grid-cols-4 lg:gap-x-8">
            {values.map((val, idx) => (
              <div
                key={idx}
                className={`flex flex-col ${
                  idx === 0
                    ? ''
                    : idx === 1
                    ? 'sm:border-l sm:border-zinc-800 sm:pl-12 lg:pl-8'
                    : idx === 2
                    ? 'border-t border-zinc-900 pt-8 sm:border-t-0 sm:pt-0 lg:border-l lg:border-zinc-800 lg:pl-8'
                    : 'border-t border-zinc-900 pt-8 sm:border-t-0 sm:pt-0 sm:border-l sm:border-zinc-800 sm:pl-12 lg:pl-8'
                }`}
              >
                <div className="text-white mb-4">
                  {val.icon}
                </div>
                <h3 className="text-lg font-bold text-white">{val.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1400,quality=80,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/24ca6849-ad7d-45c7-801d-a058c82542ed.jpg"
            alt="DJ Ron9 Live Crowd"
            loading="lazy"
            decoding="async"
            width="1200"
            height="400"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl text-white">
            Lad os skabe jeres livs fest sammen
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-zinc-300 text-sm sm:text-base">
            Datoerne i højsæsonen bliver hurtigt booket. Send en uforpligtende forespørgsel i dag, og lad os tage en snak om jeres ønsker.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto rounded-full bg-[#bb9b56] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#bb9b56]/15 transition-all hover:scale-105 hover:bg-[#a88645]"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
